import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Plus, FileText, Search, Copy, Trash2, Download, Settings, ArrowLeft, Sparkles,
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
};

const ProposalsPage = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const resp = await axios.get(`${API}/proposals`, { params });
      setProposals(resp.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load proposals");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const resp = await axios.post(`${API}/proposals`, {});
      navigate(`/proposals/${resp.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create proposal");
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (p) => {
    try {
      const resp = await axios.post(`${API}/proposals/${p.id}/duplicate`);
      toast.success(`Duplicated as ${resp.data.proposal_number}`);
      navigate(`/proposals/${resp.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete proposal ${p.proposal_number}?`)) return;
    try {
      await axios.delete(`${API}/proposals/${p.id}`);
      toast.success("Deleted");
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleDownloadPdf = async (p) => {
    try {
      const resp = await axios.get(`${API}/proposals/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="proposals-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/")}
            className="text-zinc-600" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
          </Button>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00341d] flex items-center gap-2">
            <FileText className="w-6 h-6" /> Proposals
          </h1>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/proposal-settings")}
              className="border-zinc-300"
              data-testid="proposal-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-[#00341d] hover:bg-[#002816] text-white"
              data-testid="new-proposal-btn"
            >
              {creating ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : <Plus className="w-4 h-4 mr-2" />}
              New Proposal
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-zinc-100">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
              <Input
                placeholder="Search by project, client, or number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchList()}
                className="pl-9"
                data-testid="proposal-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); }}>
              <SelectTrigger className="w-44" data-testid="proposal-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchList}>Apply</Button>
          </div>

          {loading ? (
            <div className="p-12 text-center text-zinc-400">Loading...</div>
          ) : proposals.length === 0 ? (
            <div className="p-12 text-center text-zinc-400" data-testid="proposals-empty">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No proposals yet. Click <strong>New Proposal</strong> to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proposal #</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {proposals.map((p) => (
                  <TableRow key={p.id} data-testid={`proposal-row-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.proposal_number}</TableCell>
                    <TableCell className="font-medium">{p.project_name || <em className="text-zinc-400">Untitled</em>}</TableCell>
                    <TableCell>{p.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm text-zinc-500">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}
                    </TableCell>
                    <TableCell>${(p.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>
                        {p.status || "draft"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/proposals/${p.id}/edit`)} data-testid={`edit-proposal-${p.id}`}>
                        Open
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(p)} title="Download PDF">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(p)} title="Duplicate">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(p)} className="text-red-500 hover:text-red-700" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProposalsPage;
